import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TicketFormPreview.css';

/* TicketFormPreview — faithful mirror of the embedded ticket form widget
   (ticketStatic/tickets.js: MODERN_STYLE + buildAppHtml, 2026-10-06).
   Same CSS variables and derivations as the widget: --uai-primary/bg/text/
   accent/radius, container radius = radius + 4. The widget itself is the
   design authority — change it there first, mirror here second. */

const TicketFormPreview = ({ settings }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('new');

  const radius = Math.min(32, Math.max(0, parseInt(settings.border_radius, 10) || 0));
  const vars = {
    '--uai-primary': settings.primary_color || '#1f2937',
    '--uai-bg': settings.background_color || '#ffffff',
    '--uai-text': settings.text_color || '#1f2937',
    '--uai-accent': settings.accent_color || '#2563eb',
    '--uai-radius': `${radius}px`,
  };

  // The widget's own localized chrome (fr pack) — labels shown to visitors
  // when a settings text is left empty.
  const L = {
    newTicket: t('ticketFormSettings.widgetNewTicket', 'Nouveau Ticket'),
    myTickets: t('ticketFormSettings.widgetMyTickets', 'Mes Tickets'),
    name: t('ticketFormSettings.widgetName', 'Nom'),
    email: t('ticketFormSettings.widgetEmail', 'Email'),
    namePh: t('ticketFormSettings.widgetNamePh', 'Votre nom complet'),
    emailPh: t('ticketFormSettings.widgetEmailPh', 'votre.email@exemple.com'),
    subject: t('ticketFormSettings.widgetSubject', 'Objet'),
    selectTopic: t('ticketFormSettings.widgetSelectTopic', '-- Sélectionnez un sujet --'),
    orderInfo: t('ticketFormSettings.widgetOrderInfo', 'Informations de Commande (Optionnel)'),
    trackingId: t('ticketFormSettings.widgetTrackingId', 'ID de Suivi'),
    orderNumber: t('ticketFormSettings.widgetOrderNumber', 'Numéro de Commande'),
    invoiceNumber: t('ticketFormSettings.widgetInvoiceNumber', 'Numéro de Facture'),
    trackingPh: t('ticketFormSettings.widgetTrackingPh', 'Entrez votre numéro de suivi'),
    orderPh: t('ticketFormSettings.widgetOrderPh', 'Entrez votre numéro de commande'),
    invoicePh: t('ticketFormSettings.widgetInvoicePh', 'Entrez votre numéro de facture'),
    description: t('ticketFormSettings.widgetDescription', 'Description'),
    descriptionPh: t('ticketFormSettings.widgetDescriptionPh', 'Veuillez fournir les détails de votre problème...'),
    attachments: t('ticketFormSettings.widgetAttachments', 'Pièces Jointes (Optionnel)'),
    uploadFiles: t('ticketFormSettings.widgetUploadFiles', 'Télécharger des Fichiers'),
    fileLimit: t('ticketFormSettings.widgetFileLimit', 'Maximum 3 fichiers, 5Mo chacun'),
    submitTicket: t('ticketFormSettings.widgetSubmitTicket', 'Soumettre le Ticket'),
    searchEmailPh: t('ticketFormSettings.widgetSearchEmailPh', 'Entrez votre email pour voir les tickets'),
    search: t('ticketFormSettings.widgetSearch', 'Rechercher'),
    sampleSubject1: t('ticketFormSettings.widgetSample1', 'Colis perdu'),
    sampleSubject2: t('ticketFormSettings.widgetSample2', 'Retard de livraison'),
    statusNew: t('ticketFormSettings.widgetStatusNew', 'Nouveau'),
    statusResolved: t('ticketFormSettings.widgetStatusResolved', 'Résolu'),
  };

  const subjects = settings.subjects || [];
  const hasHeader = Boolean(settings.title || settings.intro_text);

  return (
    <div className="tfp-container" style={vars}>
      {hasHeader && (
        <div className="tfp-header">
          {settings.title && <h2 className="tfp-title">{settings.title}</h2>}
          {settings.intro_text && <p className="tfp-intro">{settings.intro_text}</p>}
        </div>
      )}

      <div className="tfp-tabs">
        <button
          type="button"
          className={`tfp-tab${activeTab === 'new' ? ' active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          {L.newTicket}
        </button>
        <button
          type="button"
          className={`tfp-tab${activeTab === 'list' ? ' active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          {L.myTickets}
        </button>
      </div>

      {activeTab === 'new' ? (
        <div className="tfp-body">
          <div className="tfp-row">
            <div className="tfp-field">
              <label>{L.name}</label>
              <input type="text" placeholder={L.namePh} readOnly tabIndex={-1} />
            </div>
            <div className="tfp-field">
              <label>{L.email}</label>
              <input type="text" placeholder={L.emailPh} readOnly tabIndex={-1} />
            </div>
          </div>
          <div className="tfp-field">
            <label>{L.subject}</label>
            <select defaultValue="" tabIndex={-1}>
              <option value="">{L.selectTopic}</option>
              {subjects.map((subject, index) => (
                <option key={index} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
          {settings.show_order_fields && (
            <div className="tfp-field tfp-references">
              <label>{L.orderInfo}</label>
              <div className="tfp-reference-grid">
                <div>
                  <label className="tfp-sublabel">{L.trackingId}</label>
                  <input type="text" placeholder={L.trackingPh} readOnly tabIndex={-1} />
                </div>
                <div>
                  <label className="tfp-sublabel">{L.orderNumber}</label>
                  <input type="text" placeholder={L.orderPh} readOnly tabIndex={-1} />
                </div>
                <div>
                  <label className="tfp-sublabel">{L.invoiceNumber}</label>
                  <input type="text" placeholder={L.invoicePh} readOnly tabIndex={-1} />
                </div>
              </div>
            </div>
          )}
          <div className="tfp-field">
            <label>{L.description}</label>
            <textarea placeholder={L.descriptionPh} readOnly tabIndex={-1} />
          </div>
          <div className="tfp-field">
            <label>{L.attachments}</label>
            <div className="tfp-upload-button">{L.uploadFiles}</div>
            <div className="tfp-file-limit">{L.fileLimit}</div>
          </div>
          <button type="button" className="tfp-submit" tabIndex={-1}>
            {settings.button_label || L.submitTicket}
          </button>
        </div>
      ) : (
        <div className="tfp-body">
          <div className="tfp-search">
            <input type="text" placeholder={L.searchEmailPh} readOnly tabIndex={-1} />
            <button type="button" tabIndex={-1}>{L.search}</button>
          </div>
          <div className="tfp-ticket-item">
            <div className="tfp-ticket-item-header">
              <span className="tfp-ticket-item-subject">{L.sampleSubject1}</span>
              <span className="tfp-ticket-item-date">02/10/2026</span>
            </div>
            <span className="tfp-ticket-status tfp-status-new">{L.statusNew}</span>
          </div>
          <div className="tfp-ticket-item">
            <div className="tfp-ticket-item-header">
              <span className="tfp-ticket-item-subject">{L.sampleSubject2}</span>
              <span className="tfp-ticket-item-date">28/09/2026</span>
            </div>
            <span className="tfp-ticket-status tfp-status-resolved">{L.statusResolved}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketFormPreview;
